<template>
    <section class="searchMachine">
        <!-- ---------------
            Search Error Panel
        ---------------- -->
        <div
            v-if="searchError"
            class="searchMachine__noResults -error"
        >
            <img
                src="/images/frontend/no-results.png"
                alt=""
                class="searchMachine__noResultsImage"
                aria-hidden
            >
            <h2 class="searchMachine__noResultsHeading">
                An error has occurred.
            </h2>
            <p class="searchMachine__noResultsSub">
                {{ searchError }}
            </p>
        </div>
        <!-- ---------------
            No Results Panel
        ---------------- -->
        <div
            v-else-if="showNoResults && !isSearching"
            class="searchMachine__noResults"
        >
            <img
                src="/images/frontend/no-results.png"
                alt=""
                class="searchMachine__noResultsImage"
                aria-hidden
            >
            <h2 class="searchMachine__noResultsHeading">
                We couldn’t find anything matching <b>“{{ keyword }}”</b>
            </h2>
            <p class="searchMachine__noResultsSub">
                Try adjusting your search to find what you are looking for.
            </p>
            <button
                class="button -red searchMachine__noResultsClear"
                @click="updateKeyword('')"
            >
                View All
            </button>
        </div>
        <!-- -----------------------
            Main Interface - Sidebar
        ------------------------ -->
        <template v-if="!showNoResults">
            <aside class="searchMachine__sidebar">
                <type-list v-if="showTypeList && availableTypes.length" />
                <facet-lists :available-facets="availableFacets" />
                <section class="searchMachine__showSaved">
                    <input
                        id="showSaved"
                        v-model="showSaved"
                        type="checkbox"
                        class="checkbox"
                    >
                    <label for="showSaved">{{ showSavedLabel }}</label>
                </section>
            </aside>
            <!-- -----------------------
                Main Interface
            ------------------------ -->
            <section class="searchMachine__main">
                <button
                    class="searchMachine__facetsToggle stripButton"
                    @click="toggleFacetsModal"
                >
                    <i class="fas fa-filter" />Show Filters
                </button>
                <applied-facets />
                <div class="searchMachine__meta">
                    <div class="searchMachine__resultsCount">
                        <span
                            class="count"
                            v-text="totalResults"
                        /> results
                    </div>
                    <!-- /.searchMachine__resultsCount -->
                </div>
                <!-- searchMachine__meta -->
                <!-- --------------------
                    Search Results
                 -------------------- -->
                <div
                    v-if="results.length"
                    id="searchResults"
                    class="searchMachine__results"
                >
                    <template v-for="result in results">
                        <result-card
                            v-if="result.content_type !== 'Shortcut'"
                            :key="result.slug"
                            :result="result"
                        />
                        <shortcut-result
                            v-if="result.content_type === 'Shortcut'"
                            :key="result.slug"
                            :result="result"
                        />
                    </template>
                </div>
                <!-- /.searchMachine__results -->
                <!-- --------------------
                    No results message
                 -------------------- -->
                <div class="searchMachine__noResults" v-else>
                    <img src="/images/frontend/no-results.png" alt="">
                    <h2 class="searchMachine__noResultsHeading">
                        No results found matching this set of criteria
                    </h2>
                </div>
                <pagination
                    v-if="totalPages > 1"
                    class="searchMachine__pagination pagination"
                    :page-count="totalPages"
                    :force-page="currentPage"
                    :click-handler="goToPage"
                    :no-li-surround="true"
                    page-link-class="pagination__page"
                    break-view-link-class="pagination__break"
                    prev-text="<i class='fal fa-chevron-left'></i>Back"
                    prev-link-class="pagination__back"
                    next-text="Next<i class='fal fa-chevron-right'></i>"
                    next-link-class="pagination__next"
                />
                <!--/.pagination -->
            </section>
        </template>
        <aside
            v-if="showFacetsModal"
            class="searchMachine__facetsModal"
        >
            <type-list v-if="showTypeList && availableTypes.length" />
            <facet-lists :available-facets="availableFacets" />
            <div class="searchMachine__facetsModalActions">
                <button
                    class="button -red"
                    @click="toggleFacetsModal"
                >
                    Done
                </button>
            </div>
            <!-- /.searchMachine__facetsModalActions -->
        </aside>
    </section>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import Pagination from 'vuejs-paginate';

import ResultCard from './ResultCard.vue';
import ShortcutResult from './ShortcutResult.vue';
import AppliedFacets from './AppliedFacets.vue';
import TypeList from './TypeList.vue';
import FacetLists from './FacetLists.vue';

export default {
    components: {
        AppliedFacets,
        FacetLists,
        ResultCard,
        ShortcutResult,
        TypeList,
        Pagination,
    },
    props: {
        showTypeList: {
            type: Boolean,
            default: false,
        },
        mode: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            showFacetsModal: false,
            showSavedLabels: {
                articles: 'Show only articles I have liked',
                resources: 'Show only my favorite resources',
                shortcuts: 'Show only my favorite shortcuts',
                default: 'Show liked or favorite content only',
            }
        };
    },
    computed: {
        ...mapState('search', [
            'keyword',
            'results',
            'totalResults',
            'availableFacets',
            'availableTypes',
            'currentPage',
            'totalPages',
            'isLoading',
            'isSearching',
            'searchError',
        ]),
        showSaved: {
            get() {
                return this.$store.state.search.showSaved;
            },
            set(value) {
                this.toggleShowSaved(value);
            },
        },
        showNoResults() {
            return !!(!this.totalResults && !Object.keys(this.availableFacets).length);
        },
        showSavedLabel() {
            return this.showSavedLabels[this.mode] ? this.showSavedLabels[this.mode] : this.showSavedLabels['default'];
        }
    },
    async mounted() {
        const searchParams = new URLSearchParams(window.location.search);
        if (this.mode) {
            this.setMode(this.mode);
        }
        let keyword = '';
        if (searchParams.get('keyword')) {
            keyword = searchParams.get('keyword');
        }
        await this.getFacets({
            mode: this.mode,
            keyword,
        });
        this.setInitialState(searchParams);
        this.performSearch();
    },
    methods: {
        ...mapActions('search', [
            'performSearch',
            'getFacets',
            'setInitialState',
            'setMode',
            'toggleShowSaved',
            'clearKeyword',
            'updateKeyword',
        ]),
        goToPage(page) {
            this.$store.dispatch('search/goToPage', page);
            this.$scrollTo('#searchResults', 1, {
                offset: -30,
            });
        },
        toggleFacetsModal() {
            this.showFacetsModal = !this.showFacetsModal;
            document.documentElement.classList.toggle('u-overflow-hidden');
        },
    },
};
</script>
